import { Outlet } from 'react-router-dom'
import { ErrorState, LoadingState } from '../components/States'
import { useCompanies } from '../companies/useCompanies'
import { WelcomeDialog } from '../pages/WelcomeDialog'

/**
 * Gates the routes that work *on* a QuickBooks company.
 *
 * A tenant with no connected company can sign in perfectly well and still have
 * nothing to do: every wizard needs a company to point at. Rather than blocking
 * the page outright, the app renders behind a dialog that explains the one
 * action available — so the URL stays where the user asked to be, and the moment
 * a company exists the dialog disappears and they are already in the right place.
 *
 * The dialog is deliberately not dismissable. There is no useful state behind it
 * for a tenant with no company, and `AppShell`'s top bar stays visible above the
 * scrim, so signing out is still reachable.
 *
 * A failed company fetch is *not* treated as "no companies". Blocking someone out
 * of the app because of a transient network error, with no way back except a
 * reload, is worse than letting them through to a page that may fail visibly —
 * so the error is surfaced with a retry instead.
 */
export function RequireCompany() {
  const { connected, loading, error, reload } = useCompanies()

  if (loading) return <LoadingState label="Loading your companies…" />

  if (error) return <ErrorState error={error} onRetry={reload} />

  if (connected.length === 0) {
    return (
      <>
        <Outlet />
        <WelcomeDialog />
      </>
    )
  }

  return <Outlet />
}
